
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ProgramCategory } from "@/services/db/types";
import { DEFAULT_PROGRAM_DURATIONS } from "@/services/db/constants";
import ProgramFormCard from "./ProgramFormCard";
import { addDays } from "date-fns"; // Import addDays from date-fns

interface ProgramListProps {
  programCategories: ProgramCategory[];
  programsByCategory: Record<ProgramCategory, string[]>;
}

const ProgramList: React.FC<ProgramListProps> = ({ 
  programCategories, 
  programsByCategory 
}) => {
  const form = useFormContext();
  
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "programs",
  });

  // Function to calculate completion date based on enrollment date and duration
  const calculateCompletionDate = React.useCallback((enrollmentDate: Date, duration: number): Date => {
    console.log(`Calculating completion date: ${enrollmentDate} + ${duration} days`);
    return addDays(new Date(enrollmentDate), Number(duration));
  }, []);

  const addProgram = () => {
    // Get the current date for enrollment
    const today = new Date();
    
    // Default to first category and program
    const defaultCategory = programCategories[0];
    const defaultProgram = programsByCategory[defaultCategory][0];
    const defaultDuration = DEFAULT_PROGRAM_DURATIONS[defaultProgram] || 30;
    
    // Calculate default completion date
    const defaultCompletionDate = calculateCompletionDate(today, defaultDuration);
    
    append({
      category: defaultCategory,
      title: defaultProgram,
      fee: 0,
      enrolledAt: today,
      duration: defaultDuration,
      completionDate: defaultCompletionDate,
      feeStatus: 'paid',
      nextPaymentDate: undefined,
    });
  };

  const removeProgram = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };
  
  // Handle category change to update available programs
  const handleCategoryChange = (value: string, index: number) => {
    const category = value as ProgramCategory;
    const firstProgram = programsByCategory[category][0];
    const defaultDuration = DEFAULT_PROGRAM_DURATIONS[firstProgram] || 30;
    
    form.setValue(`programs.${index}.category`, category);
    form.setValue(`programs.${index}.title`, firstProgram);
    form.setValue(`programs.${index}.duration`, defaultDuration);
    
    // Update completion date based on new duration
    const enrollmentDate = form.watch(`programs.${index}.enrolledAt`);
    if (enrollmentDate) {
      const newCompletionDate = calculateCompletionDate(enrollmentDate, defaultDuration);
      form.setValue(`programs.${index}.completionDate`, newCompletionDate);
    }
  };
  
  // Handle program change to update duration
  const handleProgramChange = (value: string, index: number) => {
    const newDuration = DEFAULT_PROGRAM_DURATIONS[value] || 30;
    
    form.setValue(`programs.${index}.title`, value);
    form.setValue(`programs.${index}.duration`, newDuration);
    
    // Update completion date based on new duration
    const enrollmentDate = form.watch(`programs.${index}.enrolledAt`);
    if (enrollmentDate) {
      const newCompletionDate = calculateCompletionDate(enrollmentDate, newDuration);
      form.setValue(`programs.${index}.completionDate`, newCompletionDate);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Programs</Label>
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={addProgram}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Program
        </Button>
      </div>

      {/* Fix the error rendering by properly type-casting and checking */}
      {form.formState.errors.programs?.root && (
        <p className="text-sm text-red-500 mt-1">
          {String(form.formState.errors.programs.root.message || '')}
        </p>
      )}

      {fields.map((field, index) => (
        <ProgramFormCard
          key={field.id}
          index={index}
          programCategories={programCategories}
          programsByCategory={programsByCategory}
          canDelete={fields.length > 1}
          onRemove={() => removeProgram(index)}
          handleCategoryChange={handleCategoryChange}
          handleProgramChange={handleProgramChange}
          calculateCompletionDate={calculateCompletionDate}
        />
      ))}
    </div>
  );
};

export default ProgramList;
